// Library Methods
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import {
  PageContainerWrapper,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Utils
import { getFormattedDisplayDateTime } from "src/utils/date/dateHelper";

const PRIVACY_POLICY_CREATED_AT = "2026-06-04";

const TermsAndConditions = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { inputBorder } = getSurfaceTokens(theme);

  const tableContent = [
    {
      title: "Devices",
      content: (
        <Typography sx={{ fontSize: "14px" }}>
          Devices means units of the following virtual reality devices and
          accessories: neo 3 pro eye, 2 controllers with batteries, case,
          charging adapter and cable (the{" "}
          <span style={{ fontWeight: 600 }}>“Device(s)”</span>)
        </Typography>
      ),
    },
    {
      title: "Trial Term",
      content: (
        <Typography sx={{ fontSize: "14px" }}>
          For subscriptions to Devices with a trial, the trial term begins on
          the Delivery Date (defined below) and continues for the period
          outlined in the Order Form and will continue for the trial period set
          forth in the Order Form (
          <span style={{ fontWeight: 600 }}>“Trial Term”</span>). Unless
          otherwise set forth in the Order Form, during the Trial Term, the
          Customer can cancel at any time and return the Device free of charge,
          so long as the Customer completes return instructions within 5
          business days of receiving return instructions via email, and the
          Customer cancels before the end of the Trial Term. If Customer does
          not complete return instructions within 5 business days, they will be
          charged for the Fees of the Subscription Term (defined below).
        </Typography>
      ),
    },
    {
      title: "Subscription Package or Purchase Package",
      content: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Subscription Package:
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Each subscription to the RetinaLogik Solution is provided on a
            subscription basis, commencing from the order date as set forth in
            the applicable Order Form and continued for the initial subscription
            term as set forth in the applicable Order Form ({" "}
            <span style={{ fontWeight: 600 }}>“Initial Subscription Term”</span>
            ). Thereafter, unless set forth in an Order Form, the subscription
            term will automatically renew for successive one (1) year renewal
            terms (on the same terms and conditions excluding Fees for then
            current Order Forms) (each a{" "}
            <span style={{ fontWeight: 600 }}>“Renewal Subscription Term”</span>
            ), unless either Party provides the other Party with written notice
            of non-renewal at least thirty (30) days prior to the end of the
            then-current subscription term (the Initial Subscription Term and
            the Renewal Subscription Terms, collectively the{" "}
            <span style={{ fontWeight: 600 }}>“Subscription Term”</span>). The
            term of each Order Form is coterminous with the Subscription Term.
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>Purchase Package:</Typography>
          <ul
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "14px",
              paddingLeft: "16px",
            }}
          >
            <li>
              • If and to the extent Devices are purchased by Customer pursuant
              to an Order Form, the purchase of such Devices will be subject to
              these Terms and Conditions including the Hardware Addendum annexed
              hereto.
            </li>
            <li>
              • The Initial Subscription Term to the RetinaLogik Test Suite
              (defined below) with respect to such purchased Devices is such
              period set forth in the Order Form.
            </li>
          </ul>
        </Box>
      ),
    },
    {
      title: "Package Ordered ",
      content: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Purchase Package which includes:
          </Typography>

          <ul
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "14px",
              paddingLeft: "16px",
            }}
          >
            <li>
              • Sale and delivery of the number of Devices ordered in the Order
              Form
            </li>
            <li>• 1-year warranty on each such purchased Device</li>
            <li>
              • Initial Subscription Term to the RetinaLogik Test Suite as set
              forth in the applicable Order Form (including the Visual Field
              Software as hosted therein), including updates made generally
              available to RetinaLogik's customers and support.
            </li>
          </ul>

          <Typography sx={{ fontSize: "14px" }}>
            Subscription Package which includes, for the duration of the
            applicable Subscription Term as set forth in the applicable Order
            Form:
          </Typography>

          <ul
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "14px",
              paddingLeft: "16px",
            }}
          >
            <li>• Delivery and lease of the number of Devices ordered</li>
            <li>
              • RetinaLogik Test Suite subscription, including software updates
              made generally available to RetinaLogik's customers and support.
            </li>
            <li>• Warranty as described in the Hardware Addendum</li>
            <li>
              • Device updates made generally available to RetinaLogik's
              customers at RetinaLogik's discretion
            </li>
          </ul>
        </Box>
      ),
    },
    {
      title: "Indication for Use and Description of the Visual Field Software",
      content: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            The <span style={{ fontWeight: 600 }}>“Visual Field Software”</span>{" "}
            is a software as a medical device solution pre- loaded to the Device
            and accessible through a web browser which provides the
            functionalities described below when used with the Device(s):
          </Typography>

          <Typography sx={{ fontSize: "14px" }}>
            The Visual Field Software exam will be recommended by an optometrist
            or a physician, who may be an ophthalmologist (an{" "}
            <span style={{ fontWeight: 600 }}>“Eye Care Professional”</span>),
            and can be delivered and monitored by any Eye Care Professional or
            their delegate. Results from the exam are intended to be reviewed by
            an Eye Care Professional and used in conjunction with other exams
            delivered at the Eye Care Professional's discretion as part of a
            vision health assessment. The results of the exam can be used to
            evaluate the visual field of the patient to help the Eye Care
            Professional determine if there are blind spots in a patient's
            vision. The foregoing is referred to as the{" "}
            <span style={{ fontWeight: 600 }}>“Indication for Use”</span>.
          </Typography>

          <Typography sx={{ fontSize: "14px" }}>
            The Visual Field Software functionality includes:
          </Typography>

          <ul
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "14px",
              paddingLeft: "16px",
            }}
          >
            <li>
              • Gathering relevant visual field information from users of the
              Device for interpretation by Eye Care Professionals;
            </li>
            <li>
              • Analytics modules and additional tests to support the detection
              of retinal conditions of users of the Device by Eye Care
              Professionals, starting with Glaucoma;
            </li>
            <li>
              • Access to results by Eye Care Professionals through dedicated
              online dashboard and to new exams.
            </li>
          </ul>

          <Typography sx={{ fontSize: "14px" }}>
            The Visual Field Software is{" "}
            <span style={{ textDecoration: "underline" }}>not</span> a sole
            diagnostic and is{" "}
            <span style={{ textDecoration: "underline" }}>not</span> intended to
            image or monitor a physiological process or condition.
          </Typography>
        </Box>
      ),
    },
    {
      title: "Additional Fees ",
      content: (
        <Typography sx={{ fontSize: "14px" }}>
          Access to additional tests and exams available through the Visual
          Field Software may be subject to additional Fees.
        </Typography>
      ),
    },
    {
      title: "Special Terms and Conditions ",
      content: (
        <Typography sx={{ fontSize: "14px" }}>
          The Terms and Conditions apply to each Order Form.
        </Typography>
      ),
    },
  ];

  return (
    <Box
      sx={{
        ...PageContainerWrapper(theme),
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography fontWeight={600}>{t("Terms and Conditions")}</Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography fontSize="14px">
            {t("Created {{ date }}", {
              date: getFormattedDisplayDateTime(
                PRIVACY_POLICY_CREATED_AT,
                i18n.language
              ),
            })}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            ORDER FORM TERMS AND CONDITIONS
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              The general terms and conditions attached hereto (
              <span style={{ fontWeight: 600 }}>
                “General Terms and Conditions”
              </span>
              ), and addenda attached hereto (each an{" "}
              <span style={{ fontWeight: 600 }}>“Addendum”</span> and
              collectively <span style={{ fontWeight: 600 }}>“Addenda”</span>),
              which may be updated from time to time, (the{" "}
              <span style={{ fontWeight: 600 }}>“Terms and Conditions”</span>,
              also called{" "}
              <span style={{ fontWeight: 600 }}>“Terms of Service”</span>), as
              well as any additional terms expressly incorporated by reference,
              will apply to the RetinaLogik Solution (defined below) as
              specified in order forms, ordering document, online registration,
              order description or order confirmation referencing these Terms
              and Conditions (
              <span style={{ fontWeight: 600 }}>“Order Forms”</span>) or the
              Dashboard, as applicable, and will take effect as of the date that
              is the earlier of the order date as set forth in the Order Form or
              the day that Customer or any Permitted User (defined below) first
              uses any portion of the RetinaLogik Solution ({" "}
              <span style={{ fontWeight: 600 }}>“Effective Date”</span>) and are
              incorporated into, and form part of, the Agreement. These Terms
              and Conditions are agreed by and between the customer completing
              the Order Form (the{" "}
              <span style={{ fontWeight: 600 }}>“Customer”</span>) and
              Retinalogik Inc., a privately held Alberta corporation with a
              principal place of business at 3655 - 36th Street NW, Calgary,
              Alberta, T2L 1Y8, Canada, (
              <span style={{ fontWeight: 600 }}>“RetinaLogik”</span>).
              RetinaLogik and the Customer, together are the{" "}
              <span style={{ fontWeight: 600 }}>“Parties”</span> and each a{" "}
              <span style={{ fontWeight: 600 }}>“Party”</span>. Capitalized
              terms not otherwise defined in these Terms and Conditions will
              have the meanings ascribed to such terms in the supply agreement
              (if any) agreed between the Parties ({" "}
              <span style={{ fontWeight: 600 }}>“Supply Agreement”</span>), the
              applicable Order Form, or the dashboard. These Order Forms, the
              Supply Agreement (if any) together with these Terms and Conditions
              (collectively, this{" "}
              <span style={{ fontWeight: 600 }}>“Agreement”</span>).
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              BY CLICKING THE “I ACCEPT” OR “I AGREE” BUTTON OR CHECKING THE BOX
              PRESENTED WITH THESE TERMS OF SERVICE OR OTHERWISE USING THE
              RETINALOGIK SOLUTION, INCLUDING LOGGING INTO THE DASHBOARD MADE
              AVAILABLE BY RETINALOGIK (THE{" "}
              <span style={{ fontWeight: 600 }}>“DASHBOARD”</span>), CUSTOMER
              HEREBY ACKNOWLEDGES THAT CUSTOMER HAS READ, ACCEPTS, AND AGREES TO
              BE BOUND BY AND COMPLY WITH THE TERMS OF SERVICE SET FORTH IN THIS
              AGREEMENT, AS AMENDED FROM TIME TO TIME IN ACCORDANCE WITH SECTION
              20.6. THESE TERMS OF SERVICE ARE ENTERED INTO BY CUSTOMER AND
              RETINALOGIK, REGARDLESS OF WHETHER THE RETINALOGIK SOLUTION,
              INCLUDING THE DEVICE OR DASHBOARD, DISPLAYS THIRD PARTY BRANDING.
              IF CUSTOMER DOES NOT ACCEPT AND AGREE TO BE BOUND BY THESE TERMS
              AND CONDITIONS, CUSTOMER WILL IMMEDIATELY CEASE ANY FURTHER USE OF
              THE RETINALOGIK SOLUTION. BY CUSTOMER'S USE OF THE RETINALOGIK
              SOLUTION, CUSTOMER REPRESENTS AND WARRANTS TO RETINALOGIK THAT:
              (A) CUSTOMER HAS THE CAPACITY TO ENTER INTO THIS LEGALLY BINDING
              AGREEMENT; AND (B) IF CUSTOMER IS USING THE RETINALOGIK SOLUTION
              ON BEHALF OF ANOTHER PERSON, CUSTOMER HAS THE AUTHORITY TO BIND
              SUCH PERSON TO THESE TERMS AND CONDITIONS AND THE AGREEMENT.
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              By completing the Order Form (which may be in the format of a
              stripe link), Customer accepts and agrees to be bound by and
              comply with the Terms and Conditions. If there is a conflict or
              inconsistency between the Order Form and the Terms and Conditions,
              these Terms and Conditions will prevail except to the extent the
              Order Form expressly refers to the provisions of the Terms and
              Conditions over which it prevails (and provided that the Order
              Form prevails only with respect to such Order Form). The
              termination or expiration of the Supply Agreement will not
              terminate these General Terms and Conditions, any then current
              Order Forms or then current subscriptions (and applicable
              Subscription Terms). If there is a conflict or inconsistency
              between the Supply Agreement and the Terms and Conditions, these
              Terms and Conditions will prevail.
            </Typography>
          </Box>
        </Box>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: `1px solid ${inputBorder}`,
          }}
        >
          <tbody>
            {tableContent.map(({ title, content }, index) => (
              <tr key={title}>
                <td
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    border: `1px solid ${inputBorder}`,
                    padding: "8px",
                    verticalAlign: "top",
                  }}
                >
                  {index + 1}. {title}
                </td>
                <td
                  style={{
                    fontSize: "14px",
                    border: `1px solid ${inputBorder}`,
                    padding: "8px",
                    verticalAlign: "top",
                  }}
                >
                  {content}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            GENERAL TERMS AND CONDITIONS
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              1. General; Addenda.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                1.1. <span style={{ fontWeight: 600 }}>General.</span> This
                Agreement sets forth the terms and conditions under which
                RetinaLogik makes available the RetinaLogik Solution. In the
                absence of any Order Form executed by both Parties, these Terms
                and Conditions do not create any business relationship or impose
                any obligation on RetinaLogik to provide any license, access,
                product, or service.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                1.2. <span style={{ fontWeight: 600 }}>Addenda.</span> Subject
                to the terms and conditions of this Agreement, RetinaLogik will
                provide Customer with the Device(s) in accordance with the
                Hardware Addendum, access to the RetinaLogik Test Suite in
                accordance with the Software-as-a-Service Addendum, which
                includes the provision of the related software-as-a-service
                (together, with any Support Services, the{" "}
                <span style={{ fontWeight: 600 }}>“Services”</span>), in each
                case, in accordance with terms and conditions of this Agreement
                and the applicable Addendum. In this Agreement, the{" "}
                <span style={{ fontWeight: 600 }}>“RetinaLogik Solution”</span>{" "}
                means the Device(s), the RetinaLogik Test Suite (which includes
                all modules offered thereunder such as the Visual Field
                Software), any documentation made available by RetinaLogik to
                Customer in relation thereto, including the Indication for Use,
                any directions for use, and all information relating to the
                intended user, the medical conditions, purposes and uses,
                performance specifications and storage conditions (the{" "}
                <span style={{ fontWeight: 600 }}>“Documentation”</span>); all
                bug fixes, patches, workarounds, updates, upgrades,
                enhancements, modifications, improvements, customizations,
                patches, bug fixes, updates, enhancements, aggregations,
                compilations, derivative works, translations and adaptations to
                the foregoing (
                <span style={{ fontWeight: 600 }}>“Modifications”</span>) and
                other new versions of the RetinaLogik Test Suite that
                RetinaLogik makes available to Customer and the Services.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              2. Proprietary Rights; Prohibited Uses.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                2.1. <span style={{ fontWeight: 600 }}>General.</span> The
                RetinaLogik Solution contains proprietary and trade secret
                information of RetinaLogik or those of its licensors. Except for
                the limited rights or licenses that RetinaLogik grants to
                Customer under an Addendum, RetinaLogik or its licensors retain
                all rights, title and interest including all intellectual
                property rights in and to: (i) the RetinaLogik Solution,
                including any and all copies made by Customer; (ii)
                RetinaLogik's Confidential Information (defined below) including
                Documentation; (iii) Aggregated Data (defined below); and (iv)
                any Modifications, improvements, customizations, patches, bug
                fixes, updates, enhancements, aggregations, compilations,
                derivative works, translations and adaptations to the foregoing
                (<span style={{ fontWeight: 600 }}>“RetinaLogik Property”</span>
                ). Customer will not use the RetinaLogik Solution for any
                purposes beyond the scope expressly set forth in this Agreement,
                including, for greater certainty, in the applicable Addendum.
                Except as expressly permitted in this Agreement, Customer will
                have no right and will not, nor will it authorize or assist any
                third party to: (A) copy all or any portion of the RetinaLogik
                Solution; (B) disassemble, reverse engineer, modify, translate,
                alter or decompile all or any portion of the RetinaLogik
                Solution or otherwise discern the source code of the RetinaLogik
                Solution; (C) adapt, modify, translate, or create derivative
                works of the RetinaLogik Solution; (D) distribute, copy, rent,
                lease, sublicense, assign, transmit, sell or otherwise transfer
                the RetinaLogik Solution or any of Customer's rights therein; or
                (E) use the RetinaLogik Solution to create, collect, transmit,
                store, use, or process any data that violates any applicable
                laws, or infringes, violates or otherwise misappropriates the
                intellectual property or other rights of any third party
                (including any moral right, privacy right or right of
                publicity). Customer must duplicate all proprietary notices and
                legends of RetinaLogik and its suppliers or licensors upon all
                copies of the RetinaLogik Solution made by Customer. Customer
                must not remove, alter, or obscure any such proprietary notice
                or legend. Nothing in this Agreement will be construed to grant
                Customer any right to obtain or use source code.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                2.2. <span style={{ fontWeight: 600 }}>Hardware.</span> Customer
                will acquire no rights to any Devices, other than the rights
                expressly granted in this Agreement or the Hardware Addendum.
                Unless in case of purchase and payment of all Devices in full,
                Customer agrees to keep all Devices free and clear of any
                encumbrance, lien, pledge, charge, mortgage or security interest
                (<span style={{ fontWeight: 600 }}>“Encumbrances”</span>), and
                physically segregated from the property of Customer and third
                parties. Should any such Encumbrances be asserted for any
                reason, RetinaLogik may, at its sole discretion require Customer
                to obtain a properly executed release of such Encumbrances
                satisfactory to RetinaLogik. Click or tap here to enter text.
                Customer will permit RetinaLogik to always have full, free and
                unfettered access to the Devices, even if there is a dispute
                between the Parties.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>3. Subcontracting.</Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              RetinaLogik may engage third parties to assist it in providing the
              RetinaLogik Solution or any part thereof. RetinaLogik will remain
              responsible for the performance of the RetinaLogik Solution by
              such subcontractors in accordance with this Agreement.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>4. User Account.</Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              In order for Customer to access and use the RetinaLogik Solution,
              RetinaLogik will issue one or more accounts, as set forth in the
              Order Form (each, a{" "}
              <span style={{ fontWeight: 600 }}>“User Account”</span>) for each
              permitted individual user as identified in the Order Form (and
              subject to the terms of the Software-as-a-Service Addendum) to
              have access to and use of the RetinaLogik Solution (each, a{" "}
              <span style={{ fontWeight: 600 }}>“Permitted User”</span>).
              Customer will ensure that Permitted Users only use the RetinaLogik
              Solution through their User Accounts. Customer will not permit any
              Permitted User to share their User Account with any other person.
              Customer will promptly notify RetinaLogik of any actual or
              suspected unauthorized use of the RetinaLogik Solution.
              RetinaLogik reserves the right to suspend, deactivate, or replace
              any User Account if it determines (acting reasonably) that such
              User Account has been used for an unauthorized purpose. Without
              limiting the generality of the foregoing, Customer is responsible
              for all acts and omissions of Permitted Users, and any act or
              omission by a Permitted User that would constitute a breach of
              this Agreement if taken by Customer will be deemed a breach of
              this Agreement by Customer.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              5. Third Party Content, Websites or Services.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              The RetinaLogik Solution may provide links or access to
              third-party content, websites, services or systems. RetinaLogik
              does not endorse any third-party content, websites, services, or
              systems, or guarantee their quality, accuracy, reliability,
              completeness, currency, timeliness, non-infringement,
              merchantability, or fitness for any purpose. Third-party content,
              websites, services, or systems are not under the control of
              RetinaLogik, and if Customer chooses to access any such content,
              websites, services, or systems Customer does so entirely at its
              own risk. Customer acknowledges that it may be required to accept
              terms and conditions applicable to third-party content, websites,
              services, or systems, that such terms may supersede the terms in
              this Agreement with respect to the use of such third-party
              content, websites, services or systems, and Customer agrees to
              accept and comply with any such terms and conditions.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>6. Fees.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                6.1. <span style={{ fontWeight: 600 }}>Fees.</span> Customer
                agrees that the rights granted under this Agreement are
                conditioned on Customer's payment of all required fees for the
                RetinaLogik Solution, whether upfront, one-time, recurring, or a
                combination of the foregoing, as specified in the Order Form (
                <span style={{ fontWeight: 600 }}>“Fees”</span>). In
                consideration for the rights granted to Customer and the
                performance of RetinaLogik's obligations under this Agreement
                (including, for greater certainty, each Addendum), Customer will
                pay RetinaLogik the Fees. Unless otherwise noted on an Order
                Form: (i) all Fees identified are in the currency set forth in
                the Order Form; (ii) Fees are non- cancelable and
                non-refundable; and (iii) quantities purchased cannot be
                decreased during the relevant Subscription Term. If Customer's
                use of the RetinaLogik Solution exceeds the service capacity set
                forth on an Order Form or otherwise requires the payment of
                additional Fees (pursuant to the terms of this Agreement),
                Customer will be billed for such usage and Customer will pay the
                additional Fees in accordance with these Terms and Conditions
                and Agreement.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                6.2.{" "}
                <span style={{ fontWeight: 600 }}>Changes to the Fees.</span>{" "}
                Unless otherwise agreed to in an Addendum, RetinaLogik reserves
                the right to change the Fees and institute new charges upon
                providing not less than 60 days prior written notice to
                Customer.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                6.3.{" "}
                <span style={{ fontWeight: 600 }}>
                  {" "}
                  Disputed Invoices or Charges.
                </span>{" "}
                If Customer believes RetinaLogik has charged or invoiced
                Customer incorrectly, Customer will contact RetinaLogik promptly
                and in any event within 15 days after being charged or receiving
                such invoice in which the error or problem appeared in order to
                request an adjustment or credit. In the event of a dispute,
                Customer will pay any undisputed amounts in accordance with the
                payment terms herein, and the Parties will discuss the disputed
                amounts in good faith in order to resolve the dispute.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                6.4. <span style={{ fontWeight: 600 }}>Payment.</span> Unless
                otherwise agreed in an Addendum, all Fees will be (i) billed in
                advance; and (ii) in the currency identified in the Order Form.
                Customer's payment is not subject to any setoff claims or rights
                of offset of any kind, including inactive use, or where access
                to or use of the RetinaLogik Solution is prevented for any
                reason, or Customer has failed to install, use or to provide
                access to any portion of the RetinaLogik Click or tap here to
                enter text. Solution. RetinaLogik reserves the right to take any
                and all appropriate action if Customer fails to pay as required
                in this Agreement, which may include withholding of Services,
                support or maintenance until Customer has paid in full amounts
                owed to RetinaLogik; and charging a late fee of 1% per month (or
                less, as per the maximum amount allowed by applicable law) on
                all past due amounts. Customer will reimburse RetinaLogik for
                all costs incurred in collecting any late payments including
                legal costs.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                6.5. <span style={{ fontWeight: 600 }}>Taxes.</span> The Fees
                set forth in this Agreement do not include applicable sales,
                use, gross receipts, value-added, GST or HST, personal property
                or other taxes (collectively{" "}
                <span style={{ fontWeight: 600 }}>“Taxes”</span>). Customer will
                be responsible for and pay all applicable Taxes, duties,
                tariffs, assessments, export and import fees or similar charges
                (including interest and penalties imposed thereon) on the
                transactions contemplated in connection with this Agreement,
                other than Taxes based on the net income or profits of
                RetinaLogik.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              7. User Data; Personal Information.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                7.1. <span style={{ fontWeight: 600 }}>User Data.</span>{" "}
                RetinaLogik will Handle (defined below) all data, information,
                records or files Customer's, Permitted User's or users of the
                Device input, load, transmit to or enter into, or that
                RetinaLogik collects from, or that is output by the RetinaLogik
                Solution, including any test results and reports (collectively,{" "}
                <span style={{ fontWeight: 600 }}>“User Data”</span>) on behalf
                of Customer or Permitted Users and in accordance with the
                RetinaLogik's{" "}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  style={{
                    fontSize: "14px",
                    color: "#E2772E",
                    textDecoration: "underline",
                  }}
                >
                  {t("Privacy Policy")}
                </Link>
                . Customer grants to RetinaLogik and its service providers a
                non-exclusive, royalty-free licence, during the Term of the
                agreement, to access, collect, store, use and process User Data
                solely: (i) to provide the Services; (ii) to comply with
                applicable law; and (iii) to de-identify or aggregate any User
                Data that is Personal Information (defined below), such that it
                cannot reasonably be associated with any identifiable person,
                for the following purposes: (a) to protect the privacy and the
                security of any person's Personal Information; (b) to conduct
                analytics and/or research in a privacy protective manner to: (I)
                better understand, improve and make available RetinaLogik's
                products and services; (II) operate and expand RetinaLogik's
                business opportunities; and (III) improve patient health
                outcomes. RetinaLogik may sublicense the foregoing rights only
                to its service providers and subcontractors that are bound by
                confidentiality and privacy obligations no less protective than
                those set out in this Agreement, and only to the extent
                necessary to provide the Services. Customer further grants to
                RetinaLogik and its service providers, and each of their
                respective licensees, successors and assigns, an irrevocable,
                perpetual, worldwide, non-exclusive, royalty-free, transferable
                and sublicensable licence to produce, access, collect, store,
                use and disclose data, information, or other materials that are
                not identified as relating to, and cannot reasonably be used to
                identify or be associated with, a particular individual or
                company, which may include de-identified User Data (whether or
                not aggregated) and aggregated User Data (such data, information
                and materials, the{" "}
                <span style={{ fontWeight: 600 }}>“Aggregated Data”</span>), and
                Aggregated Data is not subject to any obligation of return,
                deletion or destruction under this Agreement, including under
                Section 13.2.2.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                7.2.{" "}
                <span style={{ fontWeight: 600 }}>
                  Personal Information and Personal Health Information.
                </span>{" "}
                Each Party's collection, use, storage, processing, disclosure,
                transmission and deletion (
                <span style={{ fontWeight: 600 }}>“Handle”</span> or{" "}
                <span style={{ fontWeight: 600 }}>“Handling”</span>) of
                information about an identifiable individual or information that
                is subject to applicable privacy or data protection laws (
                <span style={{ fontWeight: 600 }}>“Personal Information”</span>
                ), including any User Data that may be deemed Personal
                Information, including personal health information, will be in
                accordance with industry standards and applicable privacy laws.
                Customer acknowledges and agrees that it or its Permitted Users,
                as the case may be, is and will remain the custodian (or the
                equivalent accountable entity under applicable health privacy
                laws) of all personal health information.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                7.3. <span style={{ fontWeight: 600 }}>Loss of User Data.</span>{" "}
                To the extent permitted by applicable law, RetinaLogik will not
                be liable for any failure to store, or for loss or corruption of
                User Data. RetinaLogik reserves the right to remove, edit,
                limit, or block access to the User Data at any time and from
                time to time, and to disable or terminate Customer's account in
                its sole discretion for any or no reason, including any
                violation of any provision of this Agreement. RetinaLogik has no
                obligation to display or review any User Data.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                7.4.{" "}
                <span style={{ fontWeight: 600 }}>
                  No Re-Identification; No Sale of Personal Information.
                </span>{" "}
                RetinaLogik will not, and will contractually require its service
                providers and subcontractors not to: (i) attempt to re-identify
                any Aggregated Data or associate any Aggregated Data with any
                identifiable individual, the Customer, or any Permitted User; or
                (ii) sell User Data that constitutes Personal Information.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              8. Representations and Warranties; Disclaimer.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                8.1.{" "}
                <span style={{ fontWeight: 600 }}>
                  Mutual Representations and Warranties.
                </span>{" "}
                Each Party represents, warrants, and covenants that: (i) it has
                full power and all necessary rights to enter into this
                Agreement; and (ii) it will carry out its obligations under this
                Agreement in compliance with applicable laws applicable to it.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                8.2.{" "}
                <span style={{ fontWeight: 600 }}>
                  Customer Representations.
                </span>{" "}
                Customer represents and warrants to, and covenants with
                RetinaLogik that: (i) it has the legal authority to provide all
                Personal Information provided to RetinaLogik; (ii) Customer (or
                the Permitted Users, as the case may be and as provided by
                applicable laws) is responsible for ensuring Click or tap here
                to enter text. that all applicable consents and waivers from
                clients, patients and third parties relating to the use of the
                RetinaLogik Solution and the Handling of Personal Information
                described herein; and (iii) the User Data will only contain
                Personal Information in respect of which Customer or the
                applicable Permitted User has provided all notices and
                disclosures, obtained all applicable third party consents and
                permissions and otherwise has all authority, in each case as
                required by applicable law (including, in respect of personal
                health information, applicable health privacy legislation), to
                enable RetinaLogik to provide the RetinaLogik Solution,
                including with respect to the Handling of Personal Information
                described herein.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                8.3. <span style={{ fontWeight: 600 }}>No Medical Advice.</span>{" "}
                Customer acknowledges and agrees that:
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.3.1. Customer's use of the RetinaLogik Solution does not
                change any of Customer's or any Permitted User's
                responsibilities and obligations towards their clients or
                patients;
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.3.2. The RetinaLogik Solution is not a replacement or
                substitute for the independent judgment of a qualified regulated
                health professional, including that of the Customer or any
                Permitted User;
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.3.3. RetinaLogik shall not be liable to Customer, any
                Permitted User or any other third party for any claim relating
                to negligence, professional misconduct, medical malpractice,
                improper healthcare diagnosis, prevention, mitigation or
                treatment services or any other error or omission of Customer or
                any Permitted User resulting from the Customer's or Permitted
                Users' use or reliance on the RetinaLogik Solution;
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.3.4. As between Customer and RetinaLogik, Customer is and
                remains responsible for all output generated by the RetinaLogik
                Solution and all healthcare decisions arising from or as a
                result of any output generated by the RetinaLogik Solution; and
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.3.5. As between Customer and RetinaLogik, Customer is and
                remains responsible for, and assumes the risk of, any problems
                resulting from the content, accuracy, completeness and
                consistency of User Data.
              </Typography>

              <Typography sx={{ fontSize: "14px" }}>
                8.4.{" "}
                <span style={{ fontWeight: 600 }}>
                  Software as a Medical Device.
                </span>{" "}
                The RetinaLogik Test Suite is a software as a medical device and
                is licensed by RetinaLogik to Customer in accordance with the
                requirements of Health Canada. Customer acknowledges that the
                Device does not constitute and is not regulated as a medical
                device under applicable law. The RetinaLogik Test Suite must at
                all times be used in accordance with the Documentation.
              </Typography>

              <Typography sx={{ fontSize: "14px" }}>
                8.5. <span style={{ fontWeight: 600 }}>DISCLAIMER.</span>
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.5.1. EXCEPT AS OTHERWISE EXPRESSLY PROVIDED IN THIS AGREEMENT,
                RETINALOGIK DOES NOT WARRANT THAT THE RETINALOGIK SOLUTION WILL
                BE UNINTERRUPTED OR ERROR FREE OR THAT ALL ERRORS CAN OR WILL BE
                CORRECTED; NOR DOES IT MAKE ANY WARRANTY AS TO THE RESULTS THAT
                MAY BE OBTAINED FROM USE OF THE RETINALOGIK SOLUTION. EXCEPT AS
                SPECIFICALLY PROVIDED IN THIS AGREEMENT, THE RETINALOGIK
                SOLUTION (AND ANY PART THEREOF) ARE PROVIDED “AS IS” AND “AS
                AVAILABLE”.
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                8.5.2. TO THE EXTENT PERMITTED BY APPLICABLE LAW, RETINALOGIK
                HEREBY DISCLAIMS ALL EXPRESS, IMPLIED, COLLATERAL, OR STATUTORY
                WARRANTIES, REPRESENTATIONS, AND CONDITIONS, WHETHER WRITTEN OR
                ORAL, INCLUDING ANY IMPLIED WARRANTIES OR CONDITIONS OF
                MERCHANTABILITY, MERCHANTABLE QUALITY, COMPATIBILITY, TITLE,
                NON-INFRINGEMENT, SECURITY, RELIABILITY, COMPLETENESS, QUIET
                ENJOYMENT, ACCURACY, QUALITY, INTEGRATION OR FITNESS FOR A
                PARTICULAR PURPOSE OR USE, OR ANY WARRANTIES OR CONDITIONS
                ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE. WITHOUT
                LIMITING THE GENERALITY OF ANY OF THE FOREGOING, RETINALOGIK
                EXPRESSLY DISCLAIMS ANY REPRESENTATION, CONDITION, OR WARRANTY
                THAT ANY DATA OR INFORMATION PROVIDED TO CUSTOMER IN CONNECTION
                WITH CUSTOMER'S USE OF THE RETINALOGIK SOLUTION (OR ANY PART
                THEREOF) IS ACCURATE, OR CAN OR SHOULD BE RELIED UPON BY
                CUSTOMER FOR ANY PURPOSE WHATSOEVER.
              </Typography>

              <Typography sx={{ fontSize: "14px" }}>
                8.6. Customer's Responsibilities. Unless otherwise agreed to in
                this Agreement or in an Addendum, as between RetinaLogik and
                Customer, Customer is solely responsible for: (i) purchasing and
                obtaining from third parties, and for maintaining during the
                Term, all applicable licenses and consents for third party
                technology that are needed to use the RetinaLogik Solution
                (unless provided by RetinaLogik pursuant to this Agreement and
                paid for by Customer to RetinaLogik); (ii) configuring all
                equipment, software, and systems used with the RetinaLogik
                Solution; and (iii) ensuring that Customer and all Permitted
                Users use the RetinaLogik Solution in compliance with this
                Agreement, all of the Documentation and all applicable laws,
                including applicable health privacy legislation.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              9. Indemnification by Customer.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              Customer will defend, indemnify and hold harmless RetinaLogik, and
              its officers, directors, employees and agents (
              <span style={{ fontWeight: 600 }}>“RetinaLogik Indemnitee”</span>)
              from and against any and all losses, damages, claims, actions,
              judgments, settlements, interest, awards, penalties, fines, costs,
              or expenses of whatever kind, including reasonable legal fees and
              the costs of enforcing any right to indemnification hereunder and
              the cost of pursuing any insurance providers (
              <span style={{ fontWeight: 600 }}>“Losses”</span>), incurred by a
              RetinaLogik Indemnitee arising out of or relating to any claim,
              action, demand, inquiry, audit, proceeding, or investigation of
              any nature, civil, criminal, administrative, regulatory, or other,
              whether at law, in equity or otherwise (
              <span style={{ fontWeight: 600 }}>“Action”</span>) by a third
              party, including any Permitted User, that arise from or relate to:
              (i) Customer's or Permitted Users' breach of Sections 2.1, 8.6 or
              15 of this Agreement; (ii) unauthorized use of the RetinaLogik
              Solution by Customer or any Permitted User; (iii) User Data or the
              Handling of User Data by Customer or Permitted Users, including in
              connection with the use of the RetinaLogik Solution; (iv) a third
              party's product, software, hardware, data, or processes; (v) any
              personal injury or death arising out of Customer's or any
              Permitted User's use of the RetinaLogik Solution; (vi) any damage,
              destruction, or loss of the Devices; or (vii) use of the
              RetinaLogik Solution (or any part thereof) by Customer or any
              Permitted User in combination with any third-party software,
              hardware, application or service not provided by RetinaLogik or
              made available pursuant to this Agreement. Customer will fully
              cooperate with RetinaLogik in the defence of any claim defended by
              Customer pursuant to its indemnification obligations under this
              Agreement and will not settle any such claim without the prior
              written consent of RetinaLogik.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              10. Limitation of Liability.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                The following provisions have been negotiated by the Parties and
                reflect a fair allocation of risk and form an essential basis of
                the bargain and will survive and continue in full force and
                effect despite any failure of consideration or of an exclusive
                remedy:
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                10.1. <span style={{ fontWeight: 600 }}>LIABILITY CAP.</span>{" "}
                EXCLUDING ANY INDEMNIFICATION OBLIGATIONS, VIOLATION OR
                MISAPPROPRIATION OF INTELLECTUAL PROPERTY RIGHTS, EITHER PARTY'S
                GROSS NEGLIGENCE, WILFUL MISCONDUCT OR FRAUD, OR EITHER PARTY'S
                BREACH OF CONFIDENTIALITY (PROVIDED THAT RETINALOGIK'S TOTAL
                AGGREGATE LIABILITY FOR BREACH OF CONFIDENTIALITY RESULTING IN
                THE UNAUTHORIZED ACCESS, USE OR DISCLOSURE OF CUSTOMER'S
                CONFIDENTIAL INFORMATION IS LIMITED TO THE LIABILITY CAP), TO
                THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, IN NO EVENT
                WILL THE TOTAL AGGREGATE LIABILITY OF EITHER PARTY IN CONNECTION
                WITH OR UNDER THIS AGREEMENT, WHETHER IN CONTRACT, TORT
                (INCLUDING NEGLIGENCE OR GROSS NEGLIGENCE), OR OTHERWISE, EXCEED
                THE AMOUNT OF FEES PAID BY CUSTOMER FOR THE RETINALOGIK SOLUTION
                IN THE 12-MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING
                RISE TO THE CLAIM (
                <span style={{ fontWeight: 600 }}>“LIABILITY CAP”</span>). FOR
                GREATER CERTAINTY, THE EXISTENCE OF ONE OR MORE CLAIMS UNDER
                THIS AGREEMENT WILL NOT INCREASE THIS MAXIMUM LIABILITY AMOUNT.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                10.2.{" "}
                <span style={{ fontWeight: 600 }}>NO INDIRECT DAMAGES.</span>{" "}
                EXCLUDING ANY INDEMNIFICATION OBLIGATIONS, VIOLATION OR
                MISAPPROPRIATION OF INTELLECTUAL PROPERTY RIGHTS, EITHER PARTY'S
                GROSS NEGLIGENCE, WILFUL MISCONDUCT OR FRAUD, OR EITHER PARTY'S
                BREACH OF CONFIDENTIALITY (PROVIDED THAT RETINALOGIK'S TOTAL
                AGGREGATE LIABILITY FOR BREACH OF CONFIDENTIALITY RESULTING IN
                THE UNAUTHORIZED ACCESS, USE OR DISCLOSURE OF CUSTOMER'S
                CONFIDENTIAL INFORMATION IS LIMITED TO THE LIABILITY CAP IN
                SECTION 10.1), TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE
                LAW, IN NO EVENT WILL EITHER PARTY BE LIABLE TO THE OTHER PARTY
                FOR ANY: (I) SPECIAL, EXEMPLARY, PUNITIVE, INDIRECT, INCIDENTAL
                OR CONSEQUENTIAL DAMAGES; (II) LOST OR LOSS OF (A) SAVINGS, (B)
                PROFIT, (C) DATA, (D) USE, OR (E) GOODWILL; (III) BUSINESS
                INTERRUPTION; (IV) COSTS FOR THE PROCUREMENT OF SUBSTITUTE
                PRODUCTS OR SERVICES; (V) PERSONAL INJURY OR DEATH; OR (VI)
                PERSONAL OR PROPERTY DAMAGE ARISING OUT OF OR IN ANY WAY
                CONNECTED TO THE SERVICES OR THIS AGREEMENT, REGARDLESS OF CAUSE
                OF ACTION OR THE THEORY OF LIABILITY, WHETHER IN CONTRACT, TORT
                (INCLUDING NEGLIGENCE OR GROSS NEGLIGENCE), OR OTHERWISE, AND
                EVEN IF Click or tap here to enter text. NOTIFIED IN ADVANCE OF
                THE POSSIBILITIES OF SUCH DAMAGES.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              11. Confidentiality.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                11.1. <span style={{ fontWeight: 600 }}>Definitions.</span> For
                the purposes of this Agreement, a Party receiving Confidential
                Information (as defined below) will be the{" "}
                <span style={{ fontWeight: 600 }}>“Recipient”</span>, the Party
                disclosing such information will be the{" "}
                <span style={{ fontWeight: 600 }}>“Discloser”</span>, and{" "}
                <span style={{ fontWeight: 600 }}>
                  “Confidential Information”
                </span>{" "}
                means any and all information of Discloser or any of its
                licensors that has or will come into the possession or knowledge
                of the Recipient in connection with or as a result of entering
                into this Agreement, including without limitation information
                concerning the Discloser's past, present or future customers,
                suppliers, technology or business, provided that Discloser's
                Confidential Information does not include, except with respect
                to Personal Information: (i) information already known or
                independently developed by the Recipient without access to
                Discloser's Confidential Information; (ii) information that is
                publicly available through no wrongful act of the Recipient; or
                (iii) information received by the Recipient from a third party
                who was free to disclose it without confidentiality obligations.
                RetinaLogik Property is the Confidential Information of
                RetinaLogik.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                11.2.{" "}
                <span style={{ fontWeight: 600 }}>
                  Confidentiality Covenants.
                </span>{" "}
                The Recipient hereby agrees that during the Term and at all
                times thereafter it will not: (i) disclose Confidential
                Information of the Discloser to any person, except to its own
                personnel or affiliates having a “need to know” and that have
                entered into written agreements no less protective of such
                Confidential Information than this Agreement, and to such other
                recipients as the Discloser may approve in writing; (ii) use
                Confidential Information of the Discloser except to exercise its
                rights or perform its obligations under this Agreement; or (iii)
                alter or remove from any Confidential Information of the
                Discloser any proprietary legend. Each Party will take
                reasonable precautions to safeguard the other Party's
                Confidential Information. Those precautions will be at least as
                stringent as the precautions that the Recipient takes to protect
                its own Confidential Information of a similar type.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                11.3.{" "}
                <span style={{ fontWeight: 600 }}>
                  Exceptions to Confidentiality.
                </span>{" "}
                Notwithstanding Section 11.2, the Recipient may disclose
                Discloser's Confidential Information: (i) to the extent that
                such disclosure is required by applicable law or by the order of
                a court or similar judicial or administrative body, provided
                that, except to the extent prohibited by law, the Recipient
                promptly notifies the Discloser in writing of such required
                disclosure and cooperates with the Discloser to seek an
                appropriate protective order; (ii) to its legal counsel and
                other professional advisors if and to the extent such persons
                need to know such Confidential Information in order to provide
                applicable professional advisory services in connection with the
                Party's business; or (iii) in the case of RetinaLogik, to
                potential assignees, acquirers or successors of RetinaLogik if
                and to the extent such persons need to know such Confidential
                Information in connection with a potential sale, merger,
                amalgamation or other corporate transaction involving the
                business or assets of RetinaLogik.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                11.4.{" "}
                <span style={{ fontWeight: 600 }}>
                  Injunction and other equitable relief.
                </span>{" "}
                Each of the Parties acknowledges that disclosure of Discloser's
                Confidential Information or any other breach of this Section 11
                may cause serious and irreparable damage and harm to Discloser
                and that remedies at law may be inadequate to protect against
                breach of this Agreement, and each Party agrees that Discloser
                may seek injunctive relief for any breach of the provisions of
                this Section 11 and to the specific enforcement of the terms of
                this Section 11, in addition to any other remedy to which
                Discloser would be entitled.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                11.5.{" "}
                <span style={{ fontWeight: 600 }}>
                  Return of Confidential Information.
                </span>{" "}
                Upon written request by a Discloser or upon the termination or
                expiration of this Agreement, each Party will promptly return to
                the other Party or destroy all Confidential Information
                (excluding any User Data which is addressed at Section 13.2) of
                the other Party in its possession or control within a reasonable
                amount of time in accordance with Recipient's data destruction
                practices. Notwithstanding the foregoing, RetinaLogik may retain
                any electronically archived Customer's Confidential Information,
                provided that such retained information remains subject to the
                confidentiality obligations in this Section 11. Upon written
                request of Discloser, Recipient will certify in writing that it
                has complied with this Section 11.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>12. Term.</Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              This Agreement is effective as of the Effective Date and continues
              until all subscriptions hereunder have expired or have been
              terminated (collectively, the{" "}
              <span style={{ fontWeight: 600 }}>“Term”</span>). The Subscription
              Term of each subscription shall be as specified in the applicable
              Order Form. Except as otherwise specified in an Order Form,
              subscriptions will automatically renew for Renewal Subscription
              Terms as set forth in the cover pages hereof.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>13. Termination.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                13.1. <span style={{ fontWeight: 600 }}>Termination.</span>{" "}
                Either Party may terminate this Agreement, by giving to the
                other Party written notice of termination upon the occurrence of
                any of the following events: (i) the other Party breaches or
                defaults on any of the material terms or conditions of this
                Agreement (including Customer's payment obligations under
                Section 6) and fails to cure such breach or default within
                thirty (30) days of receipt of written notice thereof; except
                that, in the event of any breach that is incapable of being
                cured, such termination will be effective immediately; (ii) the
                other Party makes any assignment for the benefit of creditors or
                is unable to pay its debts as they mature in the ordinary course
                of business; or (iii) any proceedings are instituted by or
                against the other Party under any insolvency laws or for
                reorganization, receivership or dissolution.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                13.2.{" "}
                <span style={{ fontWeight: 600 }}>Effect of Termination.</span>
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                13.2.1. Unless otherwise specified in this Agreement, upon
                expiration or earlier termination of this Agreement, each Party
                shall: (i) promptly return to the other Party, or certify the
                destruction of any of the following of the other Party held in
                connection with the performance of this Agreement all
                Confidential Information and Personal Information (to the extent
                permitted by applicable law); and (ii) return to the other
                Party, or permit the other Party to remove, any properties of
                the other Party then situated on such Party's premises. The
                Parties agree to work in good faith to execute the foregoing in
                a timely and efficient manner.
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                13.2.2. Notwithstanding anything to the contrary in this
                Section, with respect to User Data, within thirty (30) calendar
                days following termination, RetinaLogik will, at its option,
                return to Customer (in a format mutually agreed to by the
                Parties) or delete or otherwise render inaccessible any User
                Data that remains in the hardware or systems used by RetinaLogik
                to provide the RetinaLogik Solution. After such 30-day period,
                RetinaLogik will (i) have no obligation to maintain or provide
                any User Data; (ii) accept no liability regarding, and Customer
                and Permitted Users will remain solely liable for, User Data
                backups and retention, including any requirements in relation
                thereto under applicable privacy laws; and (iii) thereafter
                delete or destroy all copies of User Data in its systems or
                otherwise in its possession or control.
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                13.2.3. In the case of Customer, upon any expiration or
                termination of this Agreement, (i) Customer must immediately
                cease accessing or using the RetinaLogik Solution; (ii)
                RetinaLogik will have no further obligation to provide the
                RetinaLogik Solution, nor to perform any Services of any kind to
                Customer; and (iii) no new Order Forms may be agreed to or
                entered into by the Parties .
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                13.2.4. Expiration or termination of this Agreement will not
                limit either Party from pursuing any other remedies available to
                it, including injunctive relief, nor will any such expiration or
                termination relieve either Party's obligation to pay all amounts
                and, in the case of Customer, Fees that have accrued or are
                otherwise owed by the Parties under this Agreement up to the
                effective date of termination.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                13.3. <span style={{ fontWeight: 600 }}>Survival.</span> Upon
                any termination or expiration of this Agreement, provisions
                contained in this Agreement that by their nature and context are
                intended to survive completion of performance, expiration,
                termination, or cancellation of this Agreement, including the
                terms on the cover pages and Sections 1.2, 6, 8.4, 9, 10, 11,
                13.2, 13.3, 19, and 20 will survive.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>14. Assignment.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                RetinaLogik may, upon giving written notice to Customer, assign
                its rights and obligations under this Agreement to any of its
                affiliates or pursuant to a merger, amalgamation or other
                corporate reorganization or a sale of substantially all of its
                assets relating to that portion of its business that delivers
                the Services. In this case, such assignee will have and may
                exercise all the rights, and will assume all of the obligations,
                of RetinaLogik under this Agreement, except that the assignment
                will not release RetinaLogik from liability for RetinaLogik's
                obligations under this Agreement. Except for such permitted
                assignment, neither Party may assign this Agreement or any
                rights or obligations under this Agreement without the prior
                written consent of each of the other Party. This Agreement
                enures to the benefit of and is binding upon the Parties and
                their respective successors and permitted assigns.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>15. Export.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Customer agrees not to directly or indirectly export, re-export
                or import any of the RetinaLogik Solution Click or tap here to
                enter text. without first obtaining RetinaLogik's written
                express consent and all required licences, permits and
                permissions. RetinaLogik makes no representation or warranty
                that the RetinaLogik Solution may be exported without Customer
                first obtaining appropriate licences or permits under applicable
                law, or that any such licence or permit has been, will be, or
                can be obtained.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>16. Force Majeure.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Neither Party will be liable for delays caused by any event or
                circumstances beyond that Party's reasonable control, including
                acts of God, acts of government, flood, fire, earthquakes, civil
                unrest, acts of terror, strikes or other labour problems (other
                than those involving that Party's employees), Internet service
                failures or delays, or the unavailability or Modification by
                third parties of telecommunications or hosting infrastructure or
                third party websites (
                <span style={{ fontWeight: 600 }}>“Force Majeure”</span>).
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>17. Notices.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                All notices required by or relating to this Agreement must be in
                writing and sent by email, by registered mail, return receipt
                requested, or via an internationally recognized express mail
                carrier (postage prepaid, return receipt requested), to the
                applicable Party to this Agreement and addressed as set forth on
                the cover pages, or to such other address as that Party may have
                given by written notice in accordance with this provision. All
                notices required by or relating to this Agreement may also be
                communicated by electronic mail, if the sender also mails a hard
                copy of such notice to the aforementioned address, except with
                respect to notices relating to Sections 6.2 (Change in Fees) of
                the Terms and Conditions, in which case an electronic mail will
                suffice and will be considered received on the next business day
                after being sent (as recorded on the device from which the
                sender sent the electronic email), unless the sender receives an
                automated message that the electronic mail has not been
                delivered.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              18. No Third-Party Beneficiaries.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                Except for those third parties that have licensed software or
                other intellectual property to RetinaLogik that is included as
                part of the RetinaLogik Solution, no person or entity will be a
                third- party beneficiary of this Agreement or have any right or
                cause of action hereunder.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>19. Governing Law.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                This Agreement shall be construed and governed by the laws of
                the Province of Alberta and the laws of Canada applicable
                therein. The Parties will initiate any lawsuits in connection
                with this Agreement in Calgary, Alberta, Canada, and irrevocably
                attorn to the exclusive personal jurisdiction and venue of the
                courts sitting therein. Notwithstanding the foregoing,
                RetinaLogik may: (i) seek remedies to collect unpaid Fees from
                Customer; and (ii) seek with respect to, a violation of
                RetinaLogik's intellectual property rights or breach of
                confidentiality, in any appropriate jurisdiction. The United
                Nations Convention on Contracts for the International Sale of
                Goods will not apply to this Agreement.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>20. General.</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                20.1. <span style={{ fontWeight: 600 }}>Severability.</span> Any
                provision of this Agreement found by a tribunal or court of
                competent jurisdiction to be illegal or unenforceable will be
                severed from this Agreement and all other provisions of this
                Agreement will remain in full force and effect.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.2. <span style={{ fontWeight: 600 }}>Waiver.</span> A waiver
                of any provision of this Agreement must be in writing and a
                waiver in one instance will not preclude enforcement of such
                provision on other occasions.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.3. <span style={{ fontWeight: 600 }}>Construction.</span>{" "}
                Except as otherwise provided in this Agreement, the Parties'
                rights and remedies under this Agreement are cumulative. The
                terms “include” and “including” mean, respectively, “include
                without limitation” and “including without limitation.” The
                headings of sections of this Agreement are for reference
                purposes only and have no substantive effect.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.4.{" "}
                <span style={{ fontWeight: 600 }}>
                  Independent Contractors.
                </span>{" "}
                RetinaLogik's relationship to Customer is that of an independent
                contractor, and neither Party is an agent or partner of the
                other. Neither Party will have, and will not represent to any
                third party that it has, any authority to act on behalf of the
                other Party.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.5. <span style={{ fontWeight: 600 }}>Entire Agreement.</span>{" "}
                This Agreement constitutes the entire agreement between the
                Parties with respect to the subject matter of this Agreement and
                supersedes all prior or contemporaneous agreements,
                representations or other communications, whether oral or
                written. If there is a conflict or inconsistency between the
                General Terms and Conditions and any Addendum, these General
                Terms and Conditions will prevail.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.6. <span style={{ fontWeight: 600 }}>Amendments.</span> No
                amendment, supplement, modification, waiver, or termination of
                this Agreement and, unless otherwise expressly specified in this
                Agreement, no consent or approval by any Party, will be binding
                unless executed in writing by the Parties. For greater
                certainty, all Addenda will, upon execution, form part of this
                Agreement. Notwithstanding the preceding sentence, RetinaLogik
                may unilaterally amend these Terms and Conditions and the
                Agreement, in whole or in part (each, an{" "}
                <span style={{ fontWeight: 600 }}>“Amendment”</span>), by giving
                Customer 30 days prior notice of such Amendment or posting
                notice of such Amendment on the RetinaLogik Test Suite or other
                RetinaLogik Solution. Unless otherwise indicated by RetinaLogik,
                any such Amendment will become effective 30 days after the date
                the notice of such Amendment is provided to Customer or is
                posted on the RetinaLogik Test Suite or other RetinaLogik
                Solution (whichever is the earlier).
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                20.7. <span style={{ fontWeight: 600 }}>Counterparts.</span>{" "}
                This Agreement may be signed in counterparts. An electronic
                signature using a qualified electronic certificate or facsimile
                signature will be treated in all respects as having the same
                effect as an original signature.
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 600 }}>
            SOFTWARE-AS-A SERVICE ADDENDUM
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>1. SaaS Addendum.</Typography>

            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              1.1{" "}
              <span style={{ fontWeight: 600 }}>Effect of this Addendum.</span>{" "}
              This software-as-a-service Addendum (
              <span style={{ fontWeight: 600 }}>
                “Software-as-a-Service Addendum”
              </span>
              ) is part of, and subject to and governed by the terms and
              conditions of, this Agreement, as amended from time to time.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              1.2 <span style={{ fontWeight: 600 }}>Definitions.</span>{" "}
              Capitalized terms used but not defined in this
              Software-as-a-Service Addendum have the meaning set forth in this
              Agreement.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              2. Provisioning of the RetinaLogik Test Suite.
            </Typography>

            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.1{" "}
              <span style={{ fontWeight: 600 }}>
                Access and Permitted Users.
              </span>{" "}
              Subject to the terms and conditions of this Agreement and provided
              Customer remains in compliance with this Agreement, during the
              term of this Agreement, RetinaLogik will make the services through
              which it hosts and makes available the RetinaLogik test suite (
              <span style={{ fontWeight: 600 }}>“RetinaLogik Test Suite”</span>)
              (as described in the Order Form) available to Customer and
              Permitted Users on the terms and conditions set forth in this
              Software-as-a- Service Addendum. For the purposes of this
              Agreement,{" "}
              <span style={{ fontWeight: 600 }}>“Permitted Users”</span> refer
              only to those individuals who are employees or contractors of
              Customer and who are: (i) licensed Eye Care Professionals or a
              regulated healthcare provider who is otherwise authorized to
              provide eye health or visual care services in the applicable
              jurisdiction of use, or (ii) acting pursuant to a delegation,
              order or directive from an Eye Care Professional.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.2{" "}
              <span style={{ fontWeight: 600 }}>Purpose and Territory.</span>{" "}
              Customer may access and use the RetinaLogik Test Suite solely in
              Canada and for use by its Permitted Users within the Province or
              Territory identified in the Location(s) of Use set forth in the
              Order Form in connection with (i) the provision of healthcare
              services, including eye health or visual care services to their
              clients or patients and in accordance with the Documentation; and
              (ii) the use of the Devices by such clients or patients as
              configured and supplied by RetinaLogik and in accordance with the
              Documentation. Customer acknowledges and agrees that (i) it will
              use, and it will ensure all Permitted Users use, the RetinaLogik
              Test Suite and any modules offered thereunder in accordance with
              the Documentation; and (ii) the RetinaLogik Test Suite will only
              be used in accordance with all applicable laws, including the
              applicable standards of practice, policies, guidance and position
              statements of the health regulatory authority responsible for
              regulating the healthcare service provided by the Permitted Users.
            </Typography>

            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.3{" "}
              <span style={{ fontWeight: 600 }}>
                Suspension of Access; Scheduled Downtime; Modifications.
              </span>{" "}
              RetinaLogik may from time to time and in its discretion, without
              limiting any of its other rights or remedies at law or in equity,
              under this Agreement: (i) suspend Customer's access to or use of
              the RetinaLogik Test Suite or any component thereof if Customer
              breaches this Agreement, and such breach: (a) is incapable of
              cure; or (b) being capable of cure, remains uncured seven days
              after RetinaLogik provides Customer with written notice of such
              breach; and (ii) make any modifications, improvements,
              customizations, updates and enhancements to the RetinaLogik Test
              Suite provided the RetinaLogik Test Suite continues to materially
              conform to the description set forth in the Order Form.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.4 <span style={{ fontWeight: 600 }}>Support.</span> RetinaLogik
              will provide Customer with technical support for the RetinaLogik
              Test Suite (
              <span style={{ fontWeight: 600 }}>“Support Services”</span>): (i)
              via telephone at (587) 741-4575 from 9:00 MT to 17:00 MT Monday to
              Friday (excluding statutory and civic holidays observed in
              Alberta, Canada); (ii) via email at support@retinalogik.ca; and
              (iii) via text messages at 587-225-0731.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.5 <span style={{ fontWeight: 600 }}>Fees and Payments.</span> In
              consideration for the access granted to Customer, Customer will
              pay to RetinaLogik the Fees set forth in the Order Form.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.6 <span style={{ fontWeight: 600 }}>Term.</span> This
              Software-as-a-Service Addendum will become effective upon the
              Effective Date and will continue for the Term, unless earlier
              terminated in accordance with the terms of this Agreement. For
              clarity, this Software-as-a-Service Addendum will terminate upon
              any termination or expiration of these General Terms and
              Conditions. Upon the Click or tap here to enter text. termination
              or expiry of this Software-as-a-Service Addendum, Customer will
              immediately cease accessing or using the RetinaLogik Solution.
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 600 }}>HARDWARE ADDENDUM</Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              1. Hardware Addendum.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              1.1{" "}
              <span style={{ fontWeight: 600 }}>Effect of this Addendum.</span>{" "}
              This hardware Addendum (
              <span style={{ fontWeight: 600 }}>“Hardware Addendum”</span>) is
              part of, and subject to and governed by the terms and conditions
              of, this Agreement, as amended from time to time.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              1.2 <span style={{ fontWeight: 600 }}>Definitions.</span>{" "}
              Capitalized terms used but not defined in this Hardware Addendum
              have the meaning set forth in this Agreement.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              1.3 <span style={{ fontWeight: 600 }}>Use; Restrictions.</span>{" "}
              Customer shall use the Devices only in connection with the
              RetinaLogik Test Suite for the provision of visual care services
              to its clients or patients within the Province or Territory
              identified in the Location(s) of Use set forth in the Order Form
              and in accordance with the Documentation.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              2. Delivery and Shipping.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.1 <span style={{ fontWeight: 600 }}>Delivery Date.</span>{" "}
              RetinaLogik shall deliver the Devices in the quantities and on the
              dates specified in the Order Form or as otherwise agreed in
              writing by the Parties (the{" "}
              <span style={{ fontWeight: 600 }}>“Delivery Date”</span>). If no
              Delivery Date is specified, RetinaLogik will deliver the Devices
              within a reasonable time after the receipt of the signed Order
              Form by Customer. Customer acknowledges and agrees that the
              effective Delivery Date of the Devices shall not have any impact
              on the payment of all Fees due under this Agreement.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.2 <span style={{ fontWeight: 600 }}>Delivery Location.</span>{" "}
              All Devices will be delivered by RetinaLogik, using its standard
              methods for packaging and shipping such Devices, to the Location
              of Use specified in this Order Form (the{" "}
              <span style={{ fontWeight: 600 }}>“Delivery Location”</span>)
              during Customer's normal business hours.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              2.3 <span style={{ fontWeight: 600 }}>Warranty.</span> All Devices
              are covered by the warranty provided by the original manufacturer
              or seller of the Devices (the{" "}
              <span style={{ fontWeight: 600 }}>“Warranty Provider”</span>) to
              RetinaLogik for a period of two (2) years from the Effective Date;
              and for the leased Devices, for the duration of the Term (except
              as otherwise provided by RetinaLogik in writing). To the extent
              permitted by applicable laws and the Warranty Provider,
              RetinaLogik will pass the benefit of the warranty received from
              the Warranty Provider, as the case may be, to the Customer. The
              applicable warranty documentation (the{" "}
              <span style={{ fontWeight: 600 }}>“Warranty Documentation”</span>)
              is provided in Appendix B of this Agreement and the Customer shall
              refer to the Warranty Documentation for specific warranty
              information and details, notably regarding the process for
              warranty claims.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              3.A PURCHASE PACKAGE - Terms applicable to purchased Device only
            </Typography>

            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              3.A.1{" "}
              <span style={{ fontWeight: 600 }}>Title and Risk of Loss.</span>{" "}
              Solely with respect to Devices purchased by Customer pursuant to
              an Order Form agreed by RetinaLogik, title to such purchased
              Devices passes to Customer upon delivery of the Devices at the
              Delivery Location. RetinaLogik will bear all risk of loss or
              damage to such purchased Devices until delivery of the Devices to
              the Delivery Location.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>
              3.B SUBSCRIPTION PACKAGE - Terms applicable to leased Device only
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                pl: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>
                3.B.1{" "}
                <span style={{ fontWeight: 600 }}>
                  Additional Restrictions on Use.
                </span>{" "}
                Customer shall (i) use and maintain the Devices in compliance
                with all applicable laws and the Documentation; and (ii) not
                make any repairs, alterations, additions or improvements to the
                Devices without RetinaLogik’s prior written consent. All
                repairs, alterations, additions or improvements made to the
                Devices shall belong to RetinaLogik. The Devices shall be kept
                at the Location of Use set forth in the Order Form.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                3.B.2 <span style={{ fontWeight: 600 }}>Ownership.</span> The
                Devices are, and shall remain, the property of RetinaLogik, and
                Customer shall have no right, title or interest therein or
                thereto except as expressly set forth in this Hardware Addendum.
                Customer does not acquire any ownership rights in the Devices
                and has no option to purchase same under this Agreement.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                3.B.3{" "}
                <span style={{ fontWeight: 600 }}>
                  Warranty; Loss and Damage.
                </span>{" "}
                Except as otherwise provided in the Warranty Documentation:
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                (i) Customer shall bear the entire risk of loss, theft, damage
                or destruction of the Devices from any cause whatsoever, and no
                loss, theft, damage or destruction of the Devices shall relieve
                Customer of the obligation to pay any Fees when due or to comply
                with any other obligation under this Agreement; and
              </Typography>
              <Typography sx={{ fontSize: "14px", pl: "16px" }}>
                (ii) In the event of damage to any item of the Device(s),
                Customer shall immediately cease use of the Device(s), inform
                RetinaLogik and Warranty Provider immediately of the same; at
                RetinaLogik's option, make it available for inspection and
                repair by RetinaLogik or Warranty Provider or send it to
                RetinaLogik, at its own costs for repair or replacement. If
                RetinaLogik (or Warranty Provider) determines that any item of
                the Device(s) is lost, stolen, destroyed or damaged beyond
                repair, RetinaLogik will replace and deliver a new Device, at
                Customer's costs.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                3.B.4 <span style={{ fontWeight: 600 }}>Devices Upgrades.</span>{" "}
                During the Term, RetinaLogik may decide, at its discretion, to
                provide Customer with the option to replace or upgrade the
                Devices, free of charge.
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                3.B.5 <span style={{ fontWeight: 600 }}>Termination.</span> Upon
                the expiration or termination of this Agreement, Customer, at
                its expense, shall return the Devices in good repair, ordinary
                wear and tear resulting from proper use thereof alone excepted,
                by delivering it, packed and ready for shipment, to such place
                as RetinaLogik may specify.
              </Typography>
            </Box>

            <Typography sx={{ fontWeight: 600 }}>
              4. Warranty; Disclaimer.
            </Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              EXCEPT AS OTHERWISE PROVIDED IN THE AGREEMENT OR THIS HARDWARE
              ADDENDUM, RETINALOGIK MAKES NO CONDITION OR WARRANTY WHATSOEVER
              WITH RESPECT TO THE DEVICES, INCLUDING ANY (I) CONDITION OR
              WARRANTY OF MERCHANTABLE QUALITY, MERCHANTABILITY; OR (II)
              CONDITION OR WARRANTY OF FITNESS FOR A PARTICULAR PURPOSE; OR
              (III) CONDITION OR WARRANTY OF TITLE; OR (IV) WARRANTY AGAINST
              INFRINGEMENT OF INTELLECTUAL PROPERTY RIGHTS OF A THIRD PARTY;
              WHETHER EXPRESS OR IMPLIED BY LAW, COURSE OF DEALING, COURSE OF
              PERFORMANCE, USAGE OF TRADE OR OTHERWISE.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>5. Price.</Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              Except as otherwise set forth in the Order Form, the Fees stated
              in the Order Form cover the price, or as applicable the rental
              fees, of the number of Devices specified in the Order Form. Any
              additional purchase or lease of Devices will be subject to
              additional Fees.
            </Typography>

            <Typography sx={{ fontWeight: 600 }}>6. Term.</Typography>
            <Typography sx={{ fontSize: "14px", pl: "16px" }}>
              This Hardware Addendum will become effective upon the Effective
              Date and will continue for the Term, unless earlier terminated in
              accordance with the terms of this Agreement. For clarity, this
              Hardware Addendum will terminate upon any termination or
              expiration of these General Terms and Conditions.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>APPENDIX B</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              WARRANTY DOCUMENTATION AND PROCESS
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pl: "16px",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              Warranty Starts on the Date of Purchase.
            </Typography>

            <ul
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                fontSize: "14px",
              }}
            >
              <li>
                • Covers standard hardware malfunctions (gross misuse not
                covered)
              </li>
              <li>• Covers accidental damage (Impact/Liquid)</li>
              <li>• Covers Power Surges</li>
              <li>• No Lemon Guarantee</li>
              <li>• Covers cost of shipping</li>
            </ul>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
